// src/Muestras/Drawer/sections/GeneralSection.jsx
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Autocomplete,
  Chip,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  drawerAccordionSx,
  drawerAccordionSummarySx,
  outlinedFieldSx,
  estadoChipSx,
  prioridadChipSx,
  marcaChipSx,
} from "../drawerSurfaceSx";
import {
  isChinaOnlyEstado,
  isProviderSelectableEstado,
  isArrivedEstado,
  isSentToClientEstado,
  isLeftWithClientEstado,
  sortTiposEstadoForSelect,
  formatEstadoLabel,
} from "../muestraEstadoHelpers";
import { createMuestraSupplierResponsable } from "../../../../api/Muestras/muestraSupplierResponsablesApi";
import { useTranslation } from "react-i18next";

export default function GeneralSection({
  muestra,
  setMuestra,
  tiposEstado,
  tiposMuestra,
  tiposTallas,
  tiposColores,
  tiposProveedores,
  tiposMarcas,
  tiposResponsables = [],
  onResponsableCreated,
  isEditMode,
  isReadOnly,
  shipmentStatusLocked = false,
  onTipoMuestraChange,
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const accSx = drawerAccordionSx(theme);
  const sumSx = drawerAccordionSummarySx(theme);
  const fin = outlinedFieldSx(theme);

  const selectedSampleType = tiposMuestra.find(
    (tipo) => tipo.ID_TIPO === muestra.ID_TIPO_MUESTRA,
  );
  const isKeepingType =
    (selectedSampleType?.TIPO || "").trim().toLowerCase() === "keeping";

  const sortedTiposEstado = React.useMemo(
    () => sortTiposEstadoForSelect(tiposEstado),
    [tiposEstado],
  );

  const responsableValue =
    tiposResponsables.find(
      (entry) => Number(entry.ID) === Number(muestra.ID_RESPONSABLE_SUPPLIER),
    ) || null;

  const estadoLabel = formatEstadoLabel(
    tiposEstado.find((e) => e.ID_ESTADO === muestra.ID_ESTADO)?.ESTADO || "",
    t,
  );
  const marcaLabel =
    tiposMarcas.find((m) => m.ID_MARCA === muestra.ID_Marca)?.MARCA || "";

  const handleResponsableChange = async (_, newValue) => {
    if (!newValue) {
      setMuestra((prev) => ({ ...prev, ID_RESPONSABLE_SUPPLIER: null }));
      return;
    }

    if (typeof newValue === "string") {
      const nombre = newValue.trim();
      if (!nombre) {
        setMuestra((prev) => ({ ...prev, ID_RESPONSABLE_SUPPLIER: null }));
        return;
      }

      const existing = tiposResponsables.find(
        (entry) => entry.Nombre.toLowerCase() === nombre.toLowerCase(),
      );
      if (existing) {
        setMuestra((prev) => ({
          ...prev,
          ID_RESPONSABLE_SUPPLIER: existing.ID,
        }));
        return;
      }

      try {
        const created = await createMuestraSupplierResponsable({ nombre });
        onResponsableCreated?.(created);
        setMuestra((prev) => ({
          ...prev,
          ID_RESPONSABLE_SUPPLIER: created.ID,
        }));
      } catch (error) {
        console.error("Error creating supplier responsable", error);
      }
      return;
    }

    setMuestra((prev) => ({
      ...prev,
      ID_RESPONSABLE_SUPPLIER: newValue.ID,
    }));
  };

  return (
    <Accordion defaultExpanded sx={accSx}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", mr: 0.5 }}>
          {t("Samples.generalInformation")}
        </Typography>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ flexWrap: "wrap", rowGap: 0.5, minWidth: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {estadoLabel ? (
            <Chip
              size="small"
              label={estadoLabel}
              sx={estadoChipSx(theme, estadoLabel)}
            />
          ) : null}
          {muestra.PRIORIDAD ? (
            <Chip
              size="small"
              label={
                muestra.PRIORIDAD === "Alta"
                  ? t("Samples.priorityHigh")
                  : muestra.PRIORIDAD === "Media"
                    ? t("Samples.priorityMedium")
                    : muestra.PRIORIDAD === "Normal"
                      ? t("Samples.priorityNormal")
                      : muestra.PRIORIDAD
              }
              sx={prioridadChipSx(theme, muestra.PRIORIDAD)}
            />
          ) : null}
          {marcaLabel ? (
            <Chip size="small" label={marcaLabel} sx={marcaChipSx(theme)} />
          ) : null}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 2, pb: 2 }}>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            label={t("Samples.sampleName")}
            value={muestra.MUESTRA || ""}
            onChange={(e) =>
              setMuestra((prev) => ({ ...prev, MUESTRA: e.target.value }))
            }
            fullWidth
            disabled={isReadOnly || !isKeepingType}
            sx={fin}
          />

          <TextField
            size="small"
            label={t("Samples.customerSampleName")}
            value={muestra.NombreCliente || ""}
            onChange={(e) =>
              setMuestra((prev) => ({ ...prev, NombreCliente: e.target.value }))
            }
            fullWidth
            disabled={isReadOnly}
            sx={fin}
          />
        </Box>

        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <FormControl fullWidth size="small" sx={fin}>
            <InputLabel>{t("Samples.development")}</InputLabel>
            <Select
              disabled={isReadOnly}
              label={t("Samples.development")}
              value={muestra.DEVELOPMENT || ""}
              onChange={(e) =>
                setMuestra((prev) => ({
                  ...prev,
                  DEVELOPMENT: e.target.value || null,
                }))
              }
            >
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              <MenuItem value="Own development">
                {t("Samples.developmentOwn")}
              </MenuItem>
              <MenuItem value="Client Development">
                {t("Samples.developmentClient")}
              </MenuItem>
            </Select>
          </FormControl>

          <Box
            className="responsable-supplier-editable"
            sx={{ width: "100%", minWidth: 0 }}
          >
            <Autocomplete
              freeSolo
              size="small"
              options={tiposResponsables}
              value={responsableValue}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.Nombre || ""
              }
              isOptionEqualToValue={(option, value) =>
                Number(option.ID) === Number(value.ID)
              }
              onChange={handleResponsableChange}
              onBlur={(e) => {
                const typed = e.target.value?.trim();
                if (
                  typed &&
                  (!responsableValue ||
                    responsableValue.Nombre.toLowerCase() !==
                      typed.toLowerCase())
                ) {
                  handleResponsableChange(null, typed);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t("Samples.responsableInSupplier")}
                  sx={fin}
                />
              )}
              sx={{ ...fin, width: "100%", minWidth: 0 }}
            />
          </Box>

          <FormControl fullWidth size="small" sx={fin}>
            <InputLabel>{t("Samples.priority")}</InputLabel>
            <Select
              disabled={isReadOnly}
              label={t("Samples.priority")}
              value={muestra.PRIORIDAD || ""}
              onChange={(e) =>
                setMuestra((prev) => ({
                  ...prev,
                  PRIORIDAD: e.target.value || null,
                }))
              }
            >
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              <MenuItem value="Alta">{t("Samples.priorityHigh")}</MenuItem>
              <MenuItem value="Media">{t("Samples.priorityMedium")}</MenuItem>
              <MenuItem value="Normal">{t("Samples.priorityNormal")}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box display="flex" gap={2} mt={2}>
          <FormControl fullWidth size="small" sx={fin}>
            <InputLabel>{t("Samples.Status")}</InputLabel>
            <Select
              label={t("Samples.Status")}
              value={muestra.ID_ESTADO || ""}
              onChange={(e) => {
                const raw = e.target.value;
                const nextId = raw === "" || raw == null ? null : Number(raw);
                setMuestra((prev) => ({
                  ...prev,
                  ID_ESTADO: Number.isFinite(nextId) ? nextId : raw,
                }));
              }}
              sx={fin}
            >
              {sortedTiposEstado.map((est) => {
                const disabled = shipmentStatusLocked
                  ? Number(est.ID_ESTADO) !== Number(muestra.ID_ESTADO) &&
                    !(
                      isSentToClientEstado(muestra.ID_ESTADO, tiposEstado) &&
                      isLeftWithClientEstado(est.ID_ESTADO, tiposEstado)
                    )
                  : isReadOnly
                    ? !isProviderSelectableEstado(est.ID_ESTADO, tiposEstado)
                    : isChinaOnlyEstado(est.ID_ESTADO);

                return (
                  <MenuItem
                    key={est.ID_ESTADO}
                    value={est.ID_ESTADO}
                    disabled={disabled}
                  >
                    {formatEstadoLabel(est.ESTADO, t)}
                  </MenuItem>
                );
              })}
            </Select>
            {shipmentStatusLocked ? (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ display: "block", mt: 0.5 }}
              >
                {isArrivedEstado(muestra.ID_ESTADO, tiposEstado)
                  ? t("Samples.arrivedStatusLockedHint")
                  : t("Samples.shipmentStatusLockedHint")}
              </Typography>
            ) : null}
          </FormControl>

          <FormControl fullWidth size="small" sx={fin}>
            <InputLabel>{t("Samples.sampleType")}</InputLabel>
            <Select
              disabled={isReadOnly}
              label={t("Samples.sampleType")}
              value={muestra.ID_TIPO_MUESTRA || ""}
              sx={fin}
              onChange={(e) => {
                const idTipo = e.target.value;

                const tipo = tiposMuestra.find((t) => t.ID_TIPO === idTipo);
                const abbr = tipo?.ABREVIATURA || "";

                const random = Math.random()
                  .toString(36)
                  .substring(2, 7)
                  .toUpperCase();

                const newName = `SAMP-${random}-${abbr}`;

                setMuestra((prev) => ({
                  ...prev,
                  ID_TIPO_MUESTRA: idTipo,
                  MUESTRA: newName,
                }));

                if (typeof onTipoMuestraChange === "function") {
                  onTipoMuestraChange({
                    idTipo,
                    abbr,
                    newName,
                    tipo,
                  });
                }
              }}
            >
              {tiposMuestra.map((tipo) => (
                <MenuItem key={tipo.ID_TIPO} value={tipo.ID_TIPO}>
                  {tipo.TIPO} ({tipo.ABREVIATURA})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box display="flex" gap={2} mt={2}>
          <FormControl fullWidth size="small" sx={fin}>
            <InputLabel>{t("Samples.brand")}</InputLabel>
            <Select
              disabled={isReadOnly}
              label={t("Samples.brand")}
              value={muestra.ID_Marca || ""}
              onChange={(e) =>
                setMuestra((prev) => ({ ...prev, ID_Marca: e.target.value }))
              }
              sx={fin}
            >
              {tiposMarcas.map((marca) => (
                <MenuItem key={marca.ID_MARCA} value={marca.ID_MARCA}>
                  {marca.MARCA}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={fin}>
            <InputLabel>{t("Samples.talla")}</InputLabel>
            <Select
              disabled={isReadOnly}
              label={t("Samples.talla")}
              value={muestra.ID_TALLA_BASE || ""}
              onChange={(e) =>
                setMuestra((prev) => ({
                  ...prev,
                  ID_TALLA_BASE: e.target.value,
                }))
              }
              sx={fin}
            >
              {tiposTallas.map((talla) => (
                <MenuItem key={talla.ID_TALLA} value={talla.ID_TALLA}>
                  {talla.TALLA}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box display="flex" gap={2} mt={2}>
          <FormControl fullWidth size="small" sx={fin}>
            <InputLabel>{t("Samples.color")}</InputLabel>

            <Select
              disabled={isReadOnly}
              label={t("Samples.color")}
              value={muestra.ID_COLOR_BASE || ""}
              sx={fin}
              renderValue={(selected) => {
                const colorObj = tiposColores.find(
                  (c) => c.ID_COLOR === selected,
                );
                if (!colorObj) return "";

                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: colorObj.HEX || "#ccc",
                        border: "1px solid #999",
                      }}
                    />
                    {colorObj.PANTONE}
                  </Box>
                );
              }}
              onChange={(e) =>
                setMuestra({ ...muestra, ID_COLOR_BASE: e.target.value })
              }
            >
              {tiposColores.map((c) => (
                <MenuItem key={c.ID_COLOR} value={c.ID_COLOR}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: c.HEX || "#ccc",
                        border: "1px solid #999",
                      }}
                    />
                    {c.PANTONE}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={fin}>
            <InputLabel>{t("Samples.Supplier")}</InputLabel>
            <Select
              disabled={isReadOnly}
              label={t("Samples.Supplier")}
              value={muestra.PROVEEDOR || ""}
              onChange={(e) =>
                setMuestra((prev) => ({ ...prev, PROVEEDOR: e.target.value }))
              }
              sx={fin}
            >
              {tiposProveedores.map((p) => (
                <MenuItem key={p.CODPROV} value={p.CODPROV}>
                  {p.N_PROVEEDOR}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          size="small"
          label={t("Samples.NOTAS")}
          value={muestra.NOTAS || ""}
          onChange={(e) =>
            setMuestra((prev) => ({ ...prev, NOTAS: e.target.value }))
          }
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          sx={{ mt: 2, ...fin }}
          disabled={isReadOnly}
        />
      </AccordionDetails>
    </Accordion>
  );
}
